import RuianAddressSelector from '@/components/RuianAddressSelector'
import { ConfirmModal } from '@/components/ConfirmModal'
import { DashboardHeader } from '@/containers/dashboard/DashboardHeader'
import ICOOptions from '@/containers/forms/person/ICOOptions'
import { PersonID } from '@/containers/forms/person/PersonID'
import PersonType from '@/containers/forms/person/PersonType'
import { useCreateDummyPerson, useDeleteDummyPerson, useGetDummyPersons, useUpdateDummyPerson } from '@/hooks/useDummyPersons'
import { PageWrapper } from '@/layouts/components/PageWrapper'
import {
    Box, Button, Flex, Grid, HStack, Icon, IconButton,
    Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay,
    Stack, Table, Tbody, Td, Text, Th, Thead, Tooltip, Tr, useDisclosure, useToast,
} from '@chakra-ui/react'
import { faBuilding, faBriefcase, faPlus, faUser } from '@fortawesome/pro-light-svg-icons'
import { faTrash } from '@fortawesome/pro-light-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { CheckboxFormControl, EmptyTablePlaceholder, InputFormControl, TableSkeleton } from 'BrokerTsLib/src/components'
import type { PersonAdditionalData } from 'BrokerTsLib/src/dtos'
import { Selection } from 'BrokerTsLib/src/enums/Selection'
import { useForm } from 'BrokerTsLib/src/rhf'
import { useEffect, useState } from 'react'
import { FormProvider } from 'react-hook-form'

type DummyPersonModalProps = {
    isOpen: boolean
    onClose: () => void
    person: PersonAdditionalData | null
    onSave: (data: PersonAdditionalData) => Promise<void>
    isSaving: boolean
}

export const DummyPersonModal = ({ isOpen, onClose, person, onSave, isSaving }: DummyPersonModalProps) => {
    const methods = useForm<PersonAdditionalData>({
        defaultValues: person ?? emptyPerson(),
    })
    const { control, handleSubmit, watch, reset } = methods

    useEffect(() => {
        reset(person ?? emptyPerson())
    }, [person, reset, isOpen])

    const type = watch('type')
    const isNaturalPerson = type === Selection.Person || type === Selection.SelfEmployed
    const isCompanyOrSelfEmployed = type === Selection.Company || type === Selection.SelfEmployed

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="xl" isCentered scrollBehavior="inside">
            <ModalOverlay backdropFilter="auto" backdropBlur="4px" bg="blackAlpha.400" />
            <ModalContent>
                <ModalHeader>
                    {person?.id ? 'Upravit dummy klienta' : 'Přidat dummy klienta'}
                </ModalHeader>
                <ModalCloseButton />

                <FormProvider {...methods}>
                    <ModalBody as={Stack} gap={8} pb={2} overflowX="hidden">
                        <PersonType personPrefix="" radioStackProps={{ spacing: 6 }} />

                        {isCompanyOrSelfEmployed && (
                            <CheckboxFormControl name="isVatPayer" control={control} label="Plátce DPH" />
                        )}

                        {isNaturalPerson && (
                            <Box sx={{ '& > div': { columnGap: 6, rowGap: 6 } }}>
                                <PersonID personPrefix="" fullWidth />
                            </Box>
                        )}

                        {isCompanyOrSelfEmployed && (
                            <Box sx={{ '& > div': { columnGap: '12px !important', rowGap: '16px !important' } }}>
                                <ICOOptions personPrefix="" />
                            </Box>
                        )}

                        {isNaturalPerson && (
                            <Grid templateColumns="80px 1fr 1fr 80px" gap={6}>
                                <InputFormControl name="titleBefore" control={control} label="Titul" />
                                <InputFormControl name="name" control={control} label="Jméno" />
                                <InputFormControl name="surname" control={control} label="Příjmení" />
                                <InputFormControl name="titleAfter" control={control} label="Titul za" />
                            </Grid>
                        )}

                        <Box sx={{ '& > div': { columnGap: 6, rowGap: 6 } }}>
                            <RuianAddressSelector addressPath="address" showOnlyLotNumber={false} />
                        </Box>

                        <Grid templateColumns="1fr 1fr" gap={6}>
                            <InputFormControl name="phone" control={control} label="Telefon" />
                            <InputFormControl name="email" control={control} label="Email" />
                        </Grid>
                    </ModalBody>
                </FormProvider>

                <ModalFooter gap={3}>
                    <Button variant="ghost" onClick={onClose} isDisabled={isSaving}>
                        Zrušit
                    </Button>
                    <Button
                        colorScheme="primary"
                        onClick={handleSubmit(onSave)}
                        isLoading={isSaving}
                    >
                        Uložit
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    )
}


const getDisplayContact = (p: PersonAdditionalData): string => {
    return [p.phone, p.email].filter(Boolean).join('\n') || '–'
}

const emptyPerson = (): PersonAdditionalData => ({
    id: 0,
    type: Selection.Person,
    birthNumber: null,
    name: null,
    surname: null,
    isForeigner: false,
    birthday: undefined,
    nationalityId: undefined,
    idendificationNumber: null,
    companyName: null,
    isVatPayer: null,
    address: { postCode: '', country: '', city: '' },
    titleBefore: null,
    titleAfter: null,
    phone: null,
    mobilePhonePrefix: null,
    mobilePhone: null,
    email: null,
    agreement: false,
})